import React, { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import {
  AppButton,
  AppCard,
  AppCardContent,
  AppCardFooter,
  AppCardHeader,
  AppCardTitle,
  DetailRow,
  FormField,
  IconButton,
  Input,
  NativeSelect,
  ROLE_LABELS,
  StatusBadge,
  UserAvatar,
} from '../ui';
import TicketPriority from './TicketPriority';
import {
  CATEGORY_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  browserLabel,
  categoryLabel,
  formatDateTime,
  formatDuration,
} from './supportVocabulary';
import { getTicketCreatorName } from './supportIdentity';

const draftOf = (t) => ({
  status: t.status || 'open',
  priority: t.priority || 'medium',
  category: t.category || 'other',
  assignedTo: t.assignedTo || '',
});

/** What a staff member can change. Nothing is sent until Save, and only what changed. */
function PropertiesCard({ ticket, myName, onSave }) {
  const [draft, setDraft] = useState(() => draftOf(ticket));
  const [saving, setSaving] = useState(false);
  const original = draftOf(ticket);
  const savedKey = JSON.stringify(original);

  // Follow the SAVED values — a save, a reopen, a refresh — but not every new
  // ticket object: sending a reply must not discard an edit made here first.
  useEffect(() => { setDraft(JSON.parse(savedKey)); }, [savedKey]);

  const changes = Object.fromEntries(
    Object.entries(draft).filter(([k, v]) => String(v).trim() !== String(original[k]).trim()),
  );
  const dirty = Object.keys(changes).length > 0;
  const set = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }));

  const save = async () => {
    setSaving(true);
    await onSave(changes);
    setSaving(false);
  };

  return (
    <AppCard as="section" aria-labelledby="ticket-properties-title">
      <AppCardHeader>
        <AppCardTitle as="h2" id="ticket-properties-title">Properties</AppCardTitle>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
        <FormField label="Status">
          <NativeSelect value={draft.status} onChange={set('status')} options={STATUS_OPTIONS} />
        </FormField>
        <FormField label="Priority">
          <NativeSelect value={draft.priority} onChange={set('priority')} options={PRIORITY_OPTIONS} />
        </FormField>
        <FormField label="Category">
          <NativeSelect value={draft.category} onChange={set('category')} options={CATEGORY_OPTIONS} />
        </FormField>
        <FormField label="Assignee" hint="Whoever is handling it — a name your team will recognise.">
          <Input value={draft.assignedTo} onChange={set('assignedTo')} maxLength={120} placeholder="Unassigned" />
        </FormField>
        {myName && draft.assignedTo.trim() !== myName && (
          <AppButton
            size="xs"
            variant="ghost"
            className="tw-self-start"
            onClick={() => setDraft((d) => ({ ...d, assignedTo: myName }))}
          >
            Assign to me
          </AppButton>
        )}
      </AppCardContent>
      <AppCardFooter className="tw-flex tw-justify-end tw-gap-2">
        {dirty && (
          <AppButton size="sm" variant="ghost" onClick={() => setDraft(original)} disabled={saving}>Discard</AppButton>
        )}
        <AppButton size="sm" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </AppButton>
      </AppCardFooter>
    </AppCard>
  );
}

function CopyNumber({ value, onCopied }) {
  if (!value || !navigator.clipboard) return <span className="tw-font-mono">{value || '—'}</span>;
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1">
      <span className="tw-font-mono">{value}</span>
      <IconButton
        label={`Copy ${value}`}
        icon={Copy}
        size="xs"
        onClick={() => navigator.clipboard.writeText(value).then(onCopied, () => {})}
      />
    </span>
  );
}

/**
 * TicketSidePanel — everything about a ticket that is not the conversation.
 *
 * Staff get the editable properties, who asked, the facts, and the technical
 * context captured when it was filed. A requester gets the facts only, in
 * their own words — never the page URL or browser string, which mean nothing
 * to them.
 *
 * Props:
 *   ticket, isStaff, showInstitute, myName
 *   onSave(changes) — staff property save; resolves when done
 *   onCopied()      — the ticket number reached the clipboard
 */
const TicketSidePanel = ({ ticket, isStaff, showInstitute, myName, onSave, onCopied }) => {
  const requesterName = getTicketCreatorName(ticket);
  const firstResponse = ticket.firstResponseAt
    ? formatDuration(new Date(ticket.firstResponseAt) - new Date(ticket.createdAt))
    : null;
  const replies = (ticket.comments || []).filter((c) => c.kind !== 'event' && !c.isInternal).length;
  const browser = browserLabel(ticket.browser);
  const hasTechnical = ticket.pageUrl || browser || ticket.screenResolution || ticket.appVersion;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {isStaff && <PropertiesCard ticket={ticket} myName={myName} onSave={onSave} />}

      {isStaff && (
        <AppCard as="section" aria-labelledby="ticket-requester-title">
          <AppCardHeader>
            <AppCardTitle as="h2" id="ticket-requester-title">Requester</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="tw-flex tw-items-center tw-gap-3">
              <UserAvatar name={requesterName} size="md" />
              <div className="tw-min-w-0">
                <p className="tw-m-0 tw-truncate tw-text-sm tw-font-semibold tw-text-content">{requesterName}</p>
                <p className="tw-m-0 tw-truncate tw-text-xs tw-text-content-muted">
                  {ticket.createdOnBehalf ? 'Filed on the institute’s behalf' : (ROLE_LABELS[ticket.role] || 'User')}
                </p>
              </div>
            </div>
            {showInstitute && ticket.tenantName && (
              <DetailRow className="tw-mt-2" label="Institute" value={ticket.tenantName} />
            )}
          </AppCardContent>
        </AppCard>
      )}

      <AppCard as="section" aria-labelledby="ticket-facts-title">
        <AppCardHeader>
          <AppCardTitle as="h2" id="ticket-facts-title">{isStaff ? 'Details' : 'Request details'}</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <DetailRow label="Number" value={<CopyNumber value={ticket.ticketId} onCopied={onCopied} />} />
          {!isStaff && (
            <>
              <DetailRow label="Status" value={<StatusBadge status={ticket.status} domain="supportTicket" />} />
              <DetailRow label="Priority" value={<TicketPriority priority={ticket.priority} />} />
              <DetailRow label="Category" value={categoryLabel(ticket.category)} />
              {ticket.assignedTo && <DetailRow label="Handled by" value={ticket.assignedTo} />}
            </>
          )}
          <DetailRow label="Opened" value={formatDateTime(ticket.createdAt)} />
          <DetailRow label="Last update" value={formatDateTime(ticket.updatedAt)} />
          <DetailRow label="First reply" value={firstResponse ? `after ${firstResponse}` : 'Not yet'} />
          <DetailRow label="Replies" value={replies} />
        </AppCardContent>
      </AppCard>

      {isStaff && hasTechnical && (
        <AppCard as="section" aria-labelledby="ticket-tech-title">
          <AppCardHeader>
            <AppCardTitle as="h2" id="ticket-tech-title">Technical details</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {ticket.pageUrl && (
              <div className="tw-pb-2.5">
                <p className="tw-m-0 tw-text-[0.8125rem] tw-font-medium tw-text-content-subtle">Page</p>
                <p className="tw-m-0 tw-mt-0.5 tw-break-all tw-font-mono tw-text-xs tw-text-content">{ticket.pageUrl}</p>
              </div>
            )}
            {browser && <DetailRow label="Browser" value={browser} />}
            {ticket.os && <DetailRow label="Platform" value={ticket.os} />}
            {ticket.screenResolution && <DetailRow label="Screen" value={ticket.screenResolution} />}
            {ticket.appVersion && <DetailRow label="App version" value={ticket.appVersion} />}
          </AppCardContent>
        </AppCard>
      )}
    </div>
  );
};

export default TicketSidePanel;
